<script>
import ContactService from '@/services/ContactService';
import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
import PrisonerVerificationService from '@/services/PrisonerVerificationService';
import moment from 'moment/moment';
import AdditionalVisitorDetails from '@/components/additionalVisitors/details/AdditionalVisitorDetails.vue';
import { mapState } from 'vuex';
import InCellBookingSlotsService from '@/services/InCellBookingSlotsService';
import { getDateOfYearDayAndTime } from '@/util/helpers';
import InCellVisitsService from '@/services/InCellVisitsService';
import { ERROR_CODES } from '@/util/errorCodes';
import BookingRestrictedDateService from '@/services/BookingRestrictedDateService';
import VisitorDetails from '@/components/visitors/details/VisitorDetails.vue';
import VisitorsPreviousVisits from '@/views/visitorDetails/VisitorsPreviousVisits.vue';
import FullScreenImageModal from '@/components/modals/FullScreenImageModal.vue';
import PrisonService from '@/services/PrisonService';
import { logger } from '@/util/logger';
import VisitorService from '@/services/VisitorService';

export default {
  name: 'BookInCellVisit',
  components: { VisitorsPreviousVisits, VisitorDetails, FullScreenImageModal },
  data() {
    return {
      loading: true,
      prisonerNumber: null,
      visitors: [],
      visitor: null,
      selectedVisitorId: null,
      loadingSelectedVisitorBans: false,
      selectedVisitorBans: [],
      additionalVisitors: [],
      fields: [
        {
          key: 'checkbox',
          label: '',
          thStyle: { width: '10%' },
        },
        {
          key: 'fullName',
          label: 'Name',
        },
        {
          key: 'birthDateAndAge',
          label: 'DOB',
        },
        {
          key: 'verificationStatus',
          label: 'Verification status',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      currentPage: 1,
      selectedAdditionalVisitorIds: [],
      bookingSlotsWithStock: [],
      bookingSlotDates: [],
      bookingSlotsGrouped: [],
      selectedBookingSlotId: null,
      selectedBookingSlotDate: null,
      restrictedBookingDates: [],
      prisonerVerificationRecordsResponse: null,
      selectedOver18Count: 0,
      prisonerVerificationImages: [],
      prisonerVerificationImagesLoading: false,
      prisonerVerificationImagesError: false,
      isPrisonerImageModalVisible: false,
      currentPrisonerImageUrl: '',
      recordVisit: false,
      purpleScribeEnabled: false,
      securityBand: 'High',
      adultContentSettings: 'Pause',
      invalidFaceEventAction: 'Pause',
      fundTokens: '-',
      showBookButton: true,
      showFundButton: false,
      bookingVisit: false,
      checkForAllowedVisitsPerMonth: false,
      allowedVisitsPerMonth: 2,
      loadingVisitsHadOnMonth: true,
      visitsHadThisMonth: 0,
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
  },
  async mounted() {
    try {
      this.getPrisonInfo();
      this.getFundTokens();
      this.getVisitsHad();
      await Promise.all([
        this.getRestrictedDates(),
        this.getBookingSlotsWithStock(),
      ]);
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    getPrisonInfo() {
      this.recordVisit = this.prison.default_call_recording;
      this.purpleScribeEnabled = this.prison.purple_scribe_default_option || false;
      this.securityBand = this.prison.default_security_band || 'High';
      this.adultContentSettings = this.prison.adult_content_settings || 'Pause';
      this.invalidFaceEventAction = this.prison.invalid_face_event_action || 'Pause';
      this.allowedVisitsPerMonth = this.prison.allowed_visits_per_month;
      this.checkForAllowedVisitsPerMonth = this.prison.check_for_allowed_visits_per_month;
      this.showBookButton = this.prison.allow_book_button;
      this.showFundButton = this.prison.allow_funding_in_cell_calls;
    },
    async getRestrictedDates() {
      try {
        const response = await BookingRestrictedDateService.getFutureRestrictedDates();
        this.restrictedBookingDates = response.data;
      } catch (e) {
        console.error('Error getting restricted booking dates: ', e);
        this.$toast.error('Error getting restricted booking dates', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getBookingSlotsWithStock() {
      try {
        const response = await InCellBookingSlotsService.getBookingSlotsAvailabilityForPrison(this.prison.prison_id)
        this.bookingSlotsWithStock = response.data;
        const flatten = {};
        this.bookingSlotsWithStock.forEach(bookingSlotStock => {
          if (flatten[bookingSlotStock.day_of_week] === null || flatten[bookingSlotStock.day_of_week] === undefined) {
            flatten[bookingSlotStock.day_of_week] = { day_of_week: bookingSlotStock.day_of_week, slots: [] };
          }
          flatten[bookingSlotStock.day_of_week].slots.push({
            booking_slot_id: bookingSlotStock._id,
            name: bookingSlotStock.name,
            slot_begin: bookingSlotStock.slot_begin,
            slot_end: bookingSlotStock.slot_end,
            visit_location: bookingSlotStock.visit_location,
          });
        });
        Object.keys(flatten).map(key => this.bookingSlotsGrouped.push(flatten[key]));
        // Order the grouped slots
        this.bookingSlotsGrouped.forEach(slot => {
          // eslint-disable-next-line no-param-reassign
          slot.slots = slot.slots.sort((bs1, bs2) => {
            // Fake year and day, as we only care about the slot_begin time
            const year = 2000;
            const day = 1;
            const bs1StartTime = getDateOfYearDayAndTime(year, day, bs1.slot_begin);
            const bs2StartTime = getDateOfYearDayAndTime(year, day, bs2.slot_begin);
            return bs1StartTime.getTime() - bs2StartTime.getTime();
          });
        });
        // Sort booking slots so they always show Mon-Sun
        const weekDaySorter = {
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
          sunday: 7,
        };
        this.bookingSlotsGrouped = this.bookingSlotsGrouped.sort((bsg1, bsg2) => {
          const bs1Day = bsg1.day_of_week.toLowerCase();
          const bs2Day = bsg2.day_of_week.toLowerCase();
          return weekDaySorter[bs1Day] - weekDaySorter[bs2Day];
        });
      } catch (e) {
        console.error('Error getting booking slots: ', e);
        this.$toast.error('Error getting booking slots', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    clearDateSelection() {
      // Clear selected date
      this.selectedBookingSlotDate = null;
      this.bookingSlotDates = [];
      // Update date list
      if (this.selectedBookingSlotId) {
        let dates = [];
        this.bookingSlotsWithStock.forEach(bookingSlotWithStock => {
          if (bookingSlotWithStock._id === this.selectedBookingSlotId) {
            dates = bookingSlotWithStock.stock;
          }
        });
        // Order dates, closest first
        dates.sort((d1, d2) => new Date(d2).getTime() - new Date(d1).getTime());
        this.bookingSlotDates = dates;
      }
    },
    isDateRestricted() {
      const dateToCheck = this.selectedBookingSlotDate;
      if (!dateToCheck) {
        return false;
      }

      let restricted = false;
      this.restrictedBookingDates.forEach(rDate => {
        const before = moment(dateToCheck).isSameOrBefore(rDate.end_date);
        const after = moment(dateToCheck).isSameOrAfter(rDate.start_date);

        if (before && after) {
          restricted = true;
        }
      });

      return restricted;
    },
    async searchForContacts() {
      try {
        if (!this.prisonerNumber || !this.prisonerNumber.length) {
          this.$toast.error(`Please enter a ${this.$options.filters.term('prisoner')} number`)
          return;
        }
        this.loading = true;
        this.visitor = null;

        // Resident verification check
        let verificationRecord = null;
        try {
          verificationRecord = await PrisonerVerificationService.getPrisonerVerificationRecordByDetails(this.prisonerNumber);
          this.prisonerVerificationRecordsResponse = verificationRecord;
        } catch (verificationError) {
          console.error('Error fetching resident verification records:', verificationError);
          if (verificationError.response && verificationError.response.status === 404) {
            this.prisonerVerificationRecordsResponse = null;
          } else {
            throw verificationError;
          }
        }

        if (!this.prisonerVerificationRecordsResponse) {
          this.$swal(
            `${this.$options.filters.term('prisoner', true)} Not Verified`,
            `${this.$options.filters.term('prisoner', true)}s need to be verified before they can have an in-room call, please check the prison number is correct or use the video call option to book a standard video call.`,
            'error',
          );
          this.loading = false;
          this.prisonerVerificationImages = [];
          return;
        }

        const isVerified = (this.prisonerVerificationRecordsResponse.verification_status === 'Verified');

        if (!isVerified) {
          this.$swal(
            `${this.$options.filters.term('prisoner', true)} Not Verified`,
            `The ${this.$options.filters.term('prisoner', false)} with number ${this.prisonerNumber} is not verified for in-room calls and cannot proceed with booking.\n\nPlease book a standard call instead.`,
            'error',
          );
          this.loading = false;
          this.prisonerVerificationImages = [];
          return;
        }

        const response = await ContactService.getPrisonerContacts(this.prisonerNumber);
        if (!response.data || !response.data.length) {
          this.$swal('No contacts found!', `Please check the ${this.$options.filters.term('prisoner')} number and try again.`)
        } else {
          this.$swal('Great news!', `${response.data.length} ${response.data.length <= 1 ? 'contact has' : 'contacts have'} been found. Please select one from the visitor drop down menu.`, 'success');
        }
        this.visitors = response.data;
      } catch (e) {
        console.error('Error getting prisoner: ', e);
        this.$toast.error('Error getting prisoner', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;

        const isVerifiedAfterFetch = (this.prisonerVerificationRecordsResponse && this.prisonerVerificationRecordsResponse.verification_status === 'Verified');
        if (isVerifiedAfterFetch) {
          const selfieImages = this.prisonerVerificationRecordsResponse.selfie_images || [];
          if (selfieImages.length > 0) {
            const latestImage = selfieImages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            this.prisonerVerificationImages = [latestImage];
          } else {
            this.prisonerVerificationImages = [];
          }
        } else {
          this.prisonerVerificationImages = [];
        }
        this.prisonerVerificationImagesLoading = false;
        this.prisonerVerificationImagesError = false;
      }
    },
    async selectVisitor() {
      try {
        if (!this.visitor) {
          this.selectedVisitorId = null;
          this.selectedVisitorBans = [];
          this.selectedAdditionalVisitorIds = [];
          this.loadingSelectedVisitorBans = false;
          return;
        }
        this.selectedVisitorId = this.visitor.user.user_id;
        this.additionalVisitors = [];
        await Promise.all([
          this.getAdditionalVisitors(),
          this.getSelectedVisitorsBan(),
          this.getVisitsHad(),
        ]);
      } catch (e) {
        console.error('Error selecting contact: ', e);
        this.$toast.error('Error selecting contact', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    async getAdditionalVisitors() {
      try {
        const response = await AdditionalVisitorsService.getAdditionalVisitors(this.visitor.user.user_id);
        this.additionalVisitors = response.data.map(additionalVisitor => ({
            fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
            birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
            ...additionalVisitor,
          }));
      } catch (e) {
        console.error('Error getting additional visitors: ', e);
        this.$toast.error('Error getting additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async toggleAdditionalVisitor(item) {
      const isOver18 = moment(new Date()).diff(item.dob, 'years') >= 18;

      const selected = this.selectedAdditionalVisitorIds.includes(item.additional_visitor_id);
      if (selected) {
        this.$toast.error('Additional Visitor removed');
       this.selectedAdditionalVisitorIds = this.selectedAdditionalVisitorIds.filter(id => id !== item.additional_visitor_id);

        // Decrement count if visitor was over 18
        if (isOver18) {
          this.selectedOver18Count -= 1;
        }
      } else {
        // Only allow 2 additional visitors over 18
        if (this.selectedOver18Count >= 2 && isOver18) {
          this.$toast.error('Maximum of 2 additional visitors over 18 allowed!', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
          return;
        }

        let add = true;
        if (this.isUnder18(item) && !item.safeguarded_at_prison) {
          add = await this.$swal({
            title: 'Are you sure?',
            text: `${item.firstname} ${item.lastname} is under 18 and has not completed the safeguarding checks. Are you sure you want to add them as an additional visitor?`,
            icon: 'warning',
            buttons: true,
          });
        }
        if (!add) {
          return;
        }
        this.$toast.success('Additional Visitor added');
        this.selectedAdditionalVisitorIds.push(item.additional_visitor_id);

        // Increment count if visitor is over 18
        if (isOver18) {
          this.selectedOver18Count += 1;
        }
      }
    },
    isUnder18(additionalVisitor) {
      return parseInt(moment(new Date()).diff(additionalVisitor.dob, 'years'), 10) < 18;
    },
    viewDetails(additionalVisitor) {
      this.$modal.show(AdditionalVisitorDetails, {
        additional_visitor_id: additionalVisitor.additional_visitor_id,
      }, {
        height: 'auto',
        classes: 'full-screen-modal',
        scrollable: true,
      }, {
        'before-close': () => {
          this.getSelectedVisitorsAdditionalVisitors();
        },
      });
    },
    async getSelectedVisitorsAdditionalVisitors() {
      try {
        this.loading = true;
        const response = await AdditionalVisitorsService.getAdditionalVisitors(this.visitor.user.user_id);
        this.additionalVisitors = response.data.map(additionalVisitor => ({
            fullName: `${additionalVisitor.firstname} ${additionalVisitor.lastname}`,
            birthDateAndAge: `${moment(additionalVisitor.dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(additionalVisitor.dob, 'years')})`,
            ...additionalVisitor,
          }));
      } catch (e) {
        console.error('Error getting selected additional visitors: ', e);
        this.$toast.error('Error getting selected additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    getSelectedVisitorsBan() {
      this.loadingSelectedVisitorBans = true;
      VisitorService.getVisitorBans(this.selectedVisitorId).then(res => {
        this.selectedVisitorBans = res.data;
        this.loadingSelectedVisitorBans = false;
      }).catch(err => {
        logger.error('Error getting visitor users ban', err, { visitorUserId: this.selectedVisitorId }, 'BookInCellVisit:getSelectedVisitorsBan');
        this.$toast.error('Error checking if the selected visitor is currently banned. Please refresh the page and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    isUserBannedOnBookingSlot() {
      if (this.selectedBookingSlotDate) {
        let banned = false;
        const slotDate = moment(this.selectedBookingSlotDate);
        this.selectedVisitorBans.forEach(ban => {
          const banStartDate = moment(ban.start_date);
          const banEndDate = moment(ban.end_date);
          // Note: the "[]" means inclusive checks on both start and end date
          if (slotDate.isBetween(banStartDate, banEndDate, null, '[]')) {
            banned = true;
          }
        });
        return banned;
      }
      return false;
    },
    showVisitsHad() {
      return this.prisonerNumber && this.selectedVisitorId && this.selectedBookingSlotDate;
    },
    getVisitsHad() {
      if (!this.showVisitsHad()) {
        return;
      }
      this.loadingVisitsHadOnMonth = true;
      InCellVisitsService.getNumberOfVisitsThisMonth(this.prisonerNumber, this.selectedBookingSlotDate).then(res => {
        this.visitsHadThisMonth = res.data.num_of_visits;
        this.loadingVisitsHadOnMonth = false;
      });
    },
    canBookVisit() {
      return this.prisonerNumber && this.visitor && this.selectedBookingSlotDate && this.selectedBookingSlotId && !this.isUserBannedOnBookingSlot() && !this.loadingSelectedVisitorBans;
    },
    getFundTokens() {
      PrisonService.getFundTokens().then(({ data }) => {
        this.fundTokens = data.tokens;
      }).catch(err => {
        logger.error('Error getting fund tokens', err, null, 'BookInCellVisit:getFundTokens');
        this.$toast.error('Something went wrong getting your fund tokens', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      });
    },
    validFundTokens() {
      try {
        const fundTokens = parseInt(this.fundTokens, 10);
        return fundTokens > 0;
      } catch (err) {
        logger.error('Error parsing fund tokens', err, { fundTokensValue: this.fundTokens }, 'BookInCellVisit:validFundTokens');
        return false;
      }
    },
    setBookingVisit(value) {
      this.bookingVisit = value;
    },
    async bookAndFundVisit() {
      if (this.checkForAllowedVisitsPerMonth && this.visitsHadThisMonth >= this.allowedVisitsPerMonth) {
        this.$swal({
          title: 'Are you sure you want to book this visit?',
          text: `${this.$options.filters.term('prisoner', true)} has had ${this.visitsHadThisMonth} visits that month. They are usually only allowed ${this.allowedVisitsPerMonth}. Do you want to continue?`,
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(willContinue => {
          if (willContinue) {
            this.sendVisitRequest(true);
          }
        });
      } else {
        this.sendVisitRequest(true);
      }
    },
    async bookVisit() {
      if (this.checkForAllowedVisitsPerMonth && this.visitsHadThisMonth >= this.allowedVisitsPerMonth) {
        this.$swal({
          title: 'Are you sure you want to book this visit?',
          text: `${this.$options.filters.term('prisoner', true)} has had ${this.visitsHadThisMonth} visits that month. They are usually only allowed ${this.allowedVisitsPerMonth}. Do you want to continue?`,
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(willContinue => {
          if (willContinue) {
            this.sendVisitRequest(false);
          }
        });
      } else {
        this.sendVisitRequest(false);
      }
    },
    checkDuplicateAwaitingVisits() {
      return this.$store.dispatch('booking/checkOverlappingVisitsAwaiting', {
        prisonerNumber: this.prisonerNumber,
        slotId: this.selectedBookingSlotId,
        date: this.selectedBookingSlotDate,
      });
    },
    sendVisitRequest(funded) {
      this.checkDuplicateAwaitingVisits().then(duplicate => {
        if (duplicate) {
          this.$toast.error(`Cannot book visit as ${this.prisonerNumber} is already awaiting confirmation for another visit during the chosen booking slot`, {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        } else if (!this.bookingVisit) {
            this.setBookingVisit(true);
            const visitRequestDto = {
              visitor_id: this.visitor.user.user_id,
              contact_id: this.visitor._id,
              booked_for_booking_slot_id: this.selectedBookingSlotId,
              booked_for_booking_slot_date: this.selectedBookingSlotDate,
              additional_visitors_ids: this.selectedAdditionalVisitorIds || [],
              visit_type: 'InCell',
              funded,
              should_record: this.recordVisit || false,
              purple_scribe_enabled: (this.recordVisit && this.purpleScribeEnabled) || false,
              security_band: this.securityBand,
              adult_content_settings: this.adultContentSettings,
              invalid_face_event_action: this.invalidFaceEventAction,
              metricAction: 'WebPortalNew',
            };

            InCellVisitsService.bookVisitForVisitor(visitRequestDto).then(res => {
              this.$toast.success('In-Room Call booked', {
                toastClassName: ['toast-std', 'success-toast'],
              });
              if (funded) {
                this.fundTokens -= 1;
              }
              this.$store.dispatch('inCellVisits/setVisit', res.data);
              this.$router.push(`/in-cell-visits/${res.data._id}/details`);
            }).catch(err => {
              logger.error('Error booking in-cell calls', err, { dto: visitRequestDto }, 'BookInCellVisit:sendVisitRequest');
              if (funded) {
                this.fundTokens += 1;
              }

              const error = err && err.response && err.response.data && err.response.data.error;
              const errorMsg = error ? err.response.data.error : '';
              let msg = 'Something went wrong booking the in-room calls, please refresh the page and try again';

              if (errorMsg === ERROR_CODES.VISITOR.NOT_VERIFIED) {
                msg = 'Cannot book visit as the visitor is not verified';
              } else if (errorMsg === ERROR_CODES.VISITOR.BANNED_ON_DATE) {
                msg = 'Cannot book visit as visitor is banned on the chosen booking slot';
              } else if (errorMsg === ERROR_CODES.VISIT_STOCK.NONE_LEFT) {
                msg = 'Cannot book visit as the chosen booking slot has no stock left';
              } else if (errorMsg === ERROR_CODES.VISIT.PRISONER_BOOKED_ON_SLOT) {
                msg = `Cannot book visit as ${this.prisonerNumber} is already booked on another visit during the chosen booking slot`;
              } else if (errorMsg === ERROR_CODES.ADDITIONAL_VISITOR.NOT_VERIFIED) {
                msg = 'Unable to book with those additional visitors as one or more is not marked as verified';
              } else if (errorMsg === ERROR_CODES.PRISON.NOT_ENOUGH_FUND_TOKENS) {
                msg = 'Unable to fund visit due to a lack of fund tokens.';
              } else if (errorMsg === ERROR_CODES.VISIT.ALREADY_HAS_VISIT) {
                msg = 'Cannot book visit as visitor already has a visit on the chosen booking slot and time';
              }

              this.$toast.error(msg, {
                toastClassName: ['toast-std', 'warning-toast'],
              });
            }).finally(() => {
              this.setBookingVisit(false);
            });
          }
      });
    },
    getStatus(item) {
      return item.verification_status === 'AwaitingVerification' ? 'Awaiting Verification' : item.verification_status;
    },
    closeModals() {
      this.$bvModal.hide('modal-expand-prisoner-image');
      this.isPrisonerImageModalVisible = false;
    },
    showPrisonerImageModal(imageUrl) {
      this.currentPrisonerImageUrl = imageUrl;
      this.isPrisonerImageModalVisible = true;
      this.$bvModal.show('modal-expand-prisoner-image');
    },
  },
};
</script>

<template>
  <div>
    <h2>Book In-Room Call</h2>
    <div v-if="loading">
      <div class="col text-center mt-5">
        <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
      </div>
    </div>
    <b-row v-else class="mt-2">
      <b-col cols="6">
        <b-card>
          <!--Prisoner Number-->
          <label for="prisoner-number">
            {{ 'prisoner' | term(true) }} Number
          </label>
          <b-input-group class="mb-1">
            <b-form-input id="prisoner-number" v-model="prisonerNumber" />
            <template #append>
              <b-btn variant="primary" :disabled="loading" @click="searchForContacts">
                Search
              </b-btn>
            </template>
          </b-input-group>

          <!--Visitor-->
          <label for="visitor">
            Visitor
          </label>
          <b-form-select
            id="visitor"
            v-model="visitor"
            class="mb-1"
            :disabled="visitors.length <= 0 || loading"
            @change="selectVisitor"
          >
            <b-form-select-option v-for="item in visitors" :key="item._id" :value="item">
              {{ item.user.firstname }} {{ item.user.lastname }}
            </b-form-select-option>
          </b-form-select>

          <!--Additional Visitors-->
          <div v-if="visitor && additionalVisitors && additionalVisitors.length > 0">
            <label for="additional-visitors">
              Additional Visitors
            </label>
            <b-table
              class="data-table mb-1"
              :current-page="currentPage"
              :fields="fields"
              :items="additionalVisitors"
              hover
              responsive
              show-empty
            >
              <template #cell(checkbox)="data">
                <button
                  v-if="selectedAdditionalVisitorIds && !selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                  class="btn btn-success btn-sm"
                  @click="toggleAdditionalVisitor(data.item)"
                >
                  <span class="fa fa-plus" />
                </button>
                <button
                  v-if="selectedAdditionalVisitorIds && selectedAdditionalVisitorIds.includes(data.item.additional_visitor_id)"
                  class="btn btn-danger btn-sm"
                  @click="toggleAdditionalVisitor(data.item)"
                >
                  <span class="fa fa-minus" />
                </button>
              </template>
              <template #cell(birthDateAndAge)="data">
                {{ data.item.birthDateAndAge }}
                <span
                  v-if="isUnder18(data.item)"
                  class="badge badge-pill"
                  :class="[data.item.safeguarded_at_prison ? 'badge-success' : 'badge-danger ']"
                >
                  UNDER 18
                </span>
              </template>
              <template #cell(verificationStatus)="data">
                {{ getStatus(data.item) }}
              </template>
              <template #cell(actions)="data">
                <button class="btn btn-primary btn-sm" @click="viewDetails(data.item)">
                  <span class="fas fa-search" /> View
                </button>
              </template>
            </b-table>
          </div>

          <!--Booking Slot-->
          <label for="booking-slot">
            Booking Slot
          </label>
          <b-form-select
            id="booking-slot"
            v-model="selectedBookingSlotId"
            class="mb-1"
            :disabled="bookingSlotsGrouped.length <= 0"
            @change="clearDateSelection"
          >
            <b-form-select-option-group
              v-for="(groupedSlot, index) in bookingSlotsGrouped"
              :key="index"
              :label="groupedSlot.day_of_week"
            >
              <b-form-select-option
                v-for="slot in groupedSlot.slots"
                :key="slot.booking_slot_id"
                :value="slot.booking_slot_id"
              >
                {{ slot.name }} {{ slot.slot_begin }} - {{ slot.slot_end }}
              </b-form-select-option>
            </b-form-select-option-group>
          </b-form-select>

          <!--Booking Date-->
          <label for="booking-date">
            Booking Date
          </label>
          <p v-if="isDateRestricted()" class="text-danger">This booking date overlaps with a restricted date period.</p>
          <b-form-select
            v-model="selectedBookingSlotDate"
            class="mb-2"
            :disabled="!selectedBookingSlotId"
          >
            <b-form-select-option
              v-for="(date, index) in bookingSlotDates"
              :key="index" :value="date.date"
              :disabled="date.remaining === 0"
            >
              {{ date.date | formatDate }} ({{ date.remaining }} {{ date.remaining > 1 ? 'spaces' : 'space' }} available)
            </b-form-select-option>
          </b-form-select>

          <!-- Record video call -->
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <b-form-checkbox v-model="recordVisit" switch cy="record-call">
                  Record video call
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- Purple Scribe Enabled -->
          <div v-if="prison && prison.purple_scribe_enabled && recordVisit" class="row">
            <div class="col-12">
              <div class="form-group">
                <b-form-checkbox v-model="purpleScribeEnabled" switch cy="purple-scribe-call">
                  Transcribe with <span class="text-primary text-bold">Purple Scribe</span>
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- Security Band -->
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label class="control-label">Security Band</label>
                <select v-model="securityBand" class="form-control" cy="security-band">
                  <option value="Off">Off</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Max">Max</option>
                </select>
                <small v-if="securityBand==='Off'">In call security will be turned off. Ideal of special scenarios.</small>
                <small v-if="securityBand==='Low'">In call security will still run but at a relaxed pace. Ideal for trusted {{ 'prisoner' | term(false) }}s with children on the video call.</small>
                <small v-if="securityBand==='Medium'">Balance of in-call experience for the visitor and in call security.</small>
                <small v-if="securityBand==='High'">A high level of security. May reduce the in call experience for the visitor/{{ 'prisoner' | term(false) }}.</small>
                <small v-if="securityBand==='Max'">The highest level of security. Please note in call experience may suffer for the visitor/{{ 'prisoner' | term(false) }}.</small>
              </div>
            </div>
          </div>

          <!-- Adult Content Setting -->
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label class="control-label">Adult Content Setting</label>
                <select v-model="adultContentSettings" class="form-control" cy="adult-content">
                  <option value="Off">Off</option>
                  <option value="Alert">Alert</option>
                  <option value="Pause">Pause</option>
                </select>
                <small v-if="adultContentSettings === 'Off'">Adult content detection will be turned off and no action taken by the system.</small>
                <small v-if="adultContentSettings === 'Alert'">The master terminal will be informed of any adult content that is detected. It will be up to the staff member to take action.</small>
                <small v-if="adultContentSettings === 'Pause'">The system will automatically pause the call and warn the offending party.</small>
              </div>
            </div>
          </div>

          <!-- Invalid Face Action -->
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label class="control-label">Invalid Face Action</label>
                <select v-model="invalidFaceEventAction" class="form-control" cy="invalid-face">
                  <option value="Pause">Pause</option>
                  <option value="Alert">Alert</option>
                </select>
                <small v-if="invalidFaceEventAction === 'Pause'">An invalid face detection will cause the offending parties stream to be blurred.</small>
                <small v-if="invalidFaceEventAction === 'Alert'">The master terminal will receive a notification when an invalid face is detected. Leaving it up to the staff member to pause and resume the call.</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <p>Available Fund Tokens: <b cy="fundTokens">{{ fundTokens }}</b></p>
            </div>
          </div>
          <div class="col-12 mt-1" :class="{'disabled': bookingVisit}">
            <div v-if="showFundButton" class="row">
              <b-btn variant="success"
                     :disabled="!canBookVisit() || !validFundTokens()"
                     @click="bookAndFundVisit()"
              >
                Book and Fund (This will use 1 of your fund tokens)
              </b-btn>
            </div>
            <div v-if="showBookButton" class="row mt-1">
              <b-btn variant="primary"
                     :disabled="!canBookVisit()"
                     @click="bookVisit()"
              >
                Book (This will request a payment from the visitor)
              </b-btn>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col cols="6">
        <!--Resident Verification Images-->
        <b-card v-if="prisonerVerificationRecordsResponse && prisonerVerificationRecordsResponse.verification_status === 'Verified'">
          <b-card-title>
            {{ 'prisoner' | term(true) }} Verification Images
          </b-card-title>
          <div v-if="prisonerVerificationImagesLoading" class="text-center my-3">
            <span class="fas fa-spinner fa-spin" style="font-size: 1.5em;" /> Loading images...
          </div>
          <b-alert v-else-if="prisonerVerificationImagesError" show variant="danger">
            Error loading {{ 'prisoner' | term(false) }} verification images.
          </b-alert>
          <div v-else-if="prisonerVerificationImages.length > 0">
            <div
              v-for="imageRecord in prisonerVerificationImages"
              :key="imageRecord._id"
              class="d-flex flex-column align-items-center"
            >
              <img
                :src="imageRecord.url"
                alt="Residnet Verification"
                class="prisoner-image mb-3"
                style="max-width: 150px; height: auto; cursor: pointer;"
                @click="showPrisonerImageModal(imageRecord.url)"
              >
              <div class="detail-item mt-1 w-100">
                <span>Uploaded:</span>
                <b>{{ imageRecord.created_at | formatDate }}</b>
              </div>
            </div>
          </div>
          <p v-else class="text-center">No {{ 'prisoner' | term(false) }} verification images available.</p>
        </b-card>

        <!--Visitor Details-->
        <div v-if="visitor">
          <div class="row">
            <div class="col-12">
              <visitor-details :visitor_user_id="visitor.user.user_id" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <visitors-previous-visits
                :visitor_user_id="visitor.user.user_id"
                :prisoner_number="prisonerNumber"
              />
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-modal id="modal-expand-prisoner-image" hide-footer size="xl" centered>
      <full-screen-image-modal v-if="isPrisonerImageModalVisible" :image-url="currentPrisonerImageUrl" />
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
  .book-visit-form {
    .card-title {
      font-size: 1.25rem;
    }
    .form-group {
      margin-bottom: .5rem;
    }
    .row {
      margin-top: .5rem;
      margin-bottom: .5rem;
    }
  }

  .visitor-bans {
    .card-body {
      padding: 0;
    }
  }

.prisoner-image {
  border: none;
}

.detail-item {
  display: flex;
  justify-content: space-between;
}
</style>
